import React, { useState } from "react";
import { DEBUG_ENABLED } from "../helpers/debugEnabled";

type DebugEntry = {
  attempt: number;
  rtt_ms: number;
  match_count: number;
  top_match: string | null;
  top_score: number | null;
  top_crop: string | null;
  top_inliers: number | null;
  image_size_kb: number | null;
  capture_res: string | null;
  thumbUrl: string | null;
  timestamp: number;
  submitted_at_ms?: number | null;
  received_at_ms?: number | null;
  engine?: string | null;
  top_corners?: number[][] | null;
  consecutive_count?: number;
  consecutive_threshold?: number;
};

/** Format a Date.now()-style ms timestamp as MM:SS.mmm for at-a-glance reading. */
const formatTs = (ms: number | null | undefined): string => {
  if (ms == null) return "—";
  const d = new Date(ms);
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const mmm = String(d.getMilliseconds()).padStart(3, "0");
  return `${mm}:${ss}.${mmm}`;
};

type PhaseBreakdown = {
  camera_ms: number;
  search_ms: number;
  preload_ms: number;
  render_ms: number;
};

type DebugOverlayProps = {
  entries: DebugEntry[];
  tsttm?: number;
  tsttf?: number;
  phases?: PhaseBreakdown;
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  background: "rgba(0,0,0,0.85)",
  color: "#0f0",
  fontFamily: "monospace",
  fontSize: "11px",
  lineHeight: "1.4",
  padding: "8px",
  zIndex: 99999,
  maxHeight: "40vh",
  overflowY: "auto",
  pointerEvents: "auto",
};

const toggleStyle: React.CSSProperties = {
  position: "fixed",
  bottom: 8,
  right: 8,
  background: "rgba(0,0,0,0.7)",
  color: "#0f0",
  fontFamily: "monospace",
  fontSize: "12px",
  border: "1px solid #0f0",
  borderRadius: "4px",
  padding: "4px 8px",
  zIndex: 100000,
  cursor: "pointer",
};

export const DebugOverlay: React.FC<DebugOverlayProps> = ({ entries, tsttm, tsttf, phases }) => {
  const [collapsed, setCollapsed] = useState(false);

  if (!DEBUG_ENABLED) return null;
  if (!entries || entries.length === 0) return null;

  const lastEntry = entries[entries.length - 1];
  const avgRtt =
    entries.length > 0
      ? Math.round(entries.reduce((s, e) => s + e.rtt_ms, 0) / entries.length)
      : 0;
  const matches = entries.filter((e) => e.match_count > 0).length;
  const captureRes = entries.find((e) => e.capture_res)?.capture_res;
  // Engine that served the most-recent query — surfaced in the header so
  // we can see at a glance whether the v2 default (global) or a fallback
  // (orb / sp_fallback) handled this scan session.
  const lastEngine = [...entries].reverse().find((e) => e.engine)?.engine;

  return (
    <>
      <button style={toggleStyle} onClick={() => setCollapsed(!collapsed)}>
        {collapsed ? "DBG" : "X"}
      </button>
      {!collapsed && (
        <div style={overlayStyle}>
          <div style={{ marginBottom: "4px", color: "#ff0", fontWeight: "bold" }}>
            DEBUG | {entries.length} queries | {matches} hits | avg RTT {avgRtt}ms
            {tsttm != null && ` | TSTTM ${(tsttm / 1000).toFixed(1)}s`}
            {tsttf != null && ` | TSTTF ${(tsttf / 1000).toFixed(1)}s`}
            {captureRes && ` | ${captureRes}`}
            {lastEngine && ` | engine=${lastEngine}`}
          </div>
          {phases && (
            <div style={{ marginBottom: "4px", color: "#0ff" }}>
              phases: camera {phases.camera_ms}ms | search {phases.search_ms}ms
              {" | "}preload {phases.preload_ms}ms | render {phases.render_ms}ms
            </div>
          )}
          {entries.map((e, i) => (
            <div key={i} style={{ color: e.match_count > 0 ? "#0f0" : "#888", display: "flex", alignItems: "center", gap: "6px" }}>
              {e.thumbUrl && (
                <img src={e.thumbUrl} alt="" style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "2px", border: e.match_count > 0 ? "1px solid #0f0" : "1px solid #555" }} />
              )}
              <span>
                #{e.attempt} {formatTs(e.submitted_at_ms)}→{formatTs(e.received_at_ms)} | {e.rtt_ms}ms |{" "}
                {e.image_size_kb ? `${e.image_size_kb}KB ` : ""}
                {e.match_count > 0
                  ? `MATCH: ${e.top_match} score=${e.top_score ?? "?"} crop=${e.top_crop ?? "?"} inliers=${e.top_inliers ?? "?"}${
                      e.consecutive_count != null && e.consecutive_threshold != null
                        ? ` [${e.consecutive_count}/${e.consecutive_threshold}]`
                        : ""
                    }`
                  : e.top_match && e.top_match.startsWith("[")
                  ? e.top_match
                  : "no match"}
                {e.top_corners && e.top_corners.length === 4 && (
                  <> | corners=[
                    {e.top_corners
                      .map((p) => `(${Math.round(p[0])},${Math.round(p[1])})`)
                      .join(" ")}]
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
